import Layout from "@/components/Layout";
import { Link } from "react-router-dom";
import { ArrowRight, Calendar } from "lucide-react";
import { useTranslation } from "react-i18next";
import { articlesList } from "@/data/blogArticles";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import SEOHead from "@/components/SEOHead";
import BlogSkeleton from "@/components/BlogSkeleton";

const Blog = () => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language?.startsWith("de") ? "de-DE" : i18n.language?.startsWith("fr") ? "fr-FR" : i18n.language?.startsWith("en") ? "en-GB" : "pl-PL";

  const { data: dbArticles, isLoading } = useQuery({
    queryKey: ["blog-articles"],
    queryFn: async () => {
      const { data } = await supabase
        .from("blog_articles")
        .select("slug, title, description, date")
        .eq("published", true)
        .order("date", { ascending: false });
      return data || [];
    },
  });

  // Static articles always first (in original order), then DB articles sorted by date
  const staticArticles = articlesList.map((a) => ({
    slug: a.slug,
    title: a.titleKey,
    excerpt: a.excerptKey,
    date: a.date,
    source: "static" as const,
  }));

  const dynamicArticles = (dbArticles || []).map((a: any) => ({
    slug: a.slug,
    title: a.title,
    excerpt: a.description,
    date: a.date,
    source: "db" as const,
  })).sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  const allArticles = [...staticArticles, ...dynamicArticles];

  return (
    <Layout>
      <SEOHead
        title="Blog o naklejkach 3D – poradniki i porady dla firm"
        description="Artykuły eksperckie o naklejkach 3D zalewanych żywicą: poradniki projektowe, porównania materiałów i folii, inspiracje branżowe. Sprawdź i wyceń online."
      />
      <section className="gradient-navy py-20">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl lg:text-5xl font-bold text-primary-foreground mb-4">
            {t("blogPage.title")}<span className="text-gradient-gold">{t("blogPage.titleGold")}</span>
          </h1>
          <p className="text-primary-foreground/80 text-lg">{t("blogPage.subtitle")}</p>
        </div>
      </section>

      <section className="py-20">
        <div className="container mx-auto px-4 max-w-4xl">
          {isLoading ? <BlogSkeleton /> : <div className="space-y-6">
            {allArticles.map((a) => (
              <Link to={`/blog/${a.slug}`} key={a.slug} className="block bg-card border border-border rounded-xl p-8 hover:shadow-lg transition-shadow hover:border-accent/40">
                <div className="flex items-center gap-2 text-muted-foreground text-sm mb-3">
                  <Calendar className="w-4 h-4" />
                  {new Date(a.date).toLocaleDateString(locale, { year: "numeric", month: "long", day: "numeric" })}
                  {a.source === "db" && (
                    <span className="ml-2 inline-block w-2 h-2 rounded-full bg-accent" title="auto" />
                  )}
                </div>
                <h2 className="text-2xl font-bold text-foreground mb-3">{a.title}</h2>
                <p className="text-muted-foreground leading-relaxed mb-4">{a.excerpt}</p>
                <span className="text-accent font-semibold flex items-center gap-1 text-sm">
                  {t("blogPage.readMore")} <ArrowRight className="w-4 h-4" />
                </span>
              </Link>
            ))}
          </div>}
        </div>
      </section>
    </Layout>
  );
};

export default Blog;
